import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useInView } from 'react-intersection-observer';
import useQueryParams from '../hooks/useQueryParams';

const refetchOnMountDefaultValue = 'always';

const useInfiniteFetch = ({
  queryKey,
  fetchFunction,
  expandedRowsLimit,
  itemName,
  enabled = true,
  searchValue: inputSearchValue,
  refetchOnMount = refetchOnMountDefaultValue,
  noCacheTime = false,
  keepPreviousData = false,
}) => {
  const { ref: lastElementRef, inView } = useInView();
  const {
    queryObject: { page, limit, ...rest },
  } = useQueryParams();

  const defaultPage = page || 1;
  const defaultLimit = limit || expandedRowsLimit;

  const query = useMemo(() => {
    return {
      ...rest,
    };
  }, [rest]);

  const { data, hasNextPage, fetchNextPage, isLoading, ...props } = useInfiniteQuery(
    inputSearchValue ? [queryKey, inputSearchValue] : queryKey,
    async ({ pageParam = defaultPage, signal }) => {
      return fetchFunction({
        page: pageParam,
        limit: defaultLimit,
        signal,
        ...(inputSearchValue ? { search: inputSearchValue } : {}),
        ...query,
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const items = Array.isArray(lastPage[itemName]) ? lastPage[itemName] : [];
        if (items.length >= defaultLimit) {
          return allPages.length + 1;
        }
        return undefined;
      },
      enabled,
      refetchOnMount,
      keepPreviousData,
      ...(noCacheTime ? { cacheTime: 0 } : {}),
    },
  );

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  const mergedData = useMemo(() => {
    if (!data?.pages?.length) {
      return {
        data: [],
        count: 0,
      };
    }

    const combined = data.pages.reduce(
      (acc, page) => {
        const items = Array.isArray(page[itemName]) ? page[itemName] : [];
        acc.data = [...acc.data, ...items];
        acc.count = Math.max(acc.count, page.count || 0);
        // A total of zero is still a total, so an empty result reads as "0 results" rather than
        // as no count at all.
        if (page.totalCount != null) {
          acc.totalCount = page.totalCount;
        }
        return acc;
      },
      { data: [], count: 0 },
    );

    const lastPage = data.pages[data.pages.length - 1];
    return {
      ...lastPage,
      ...combined,
    };
  }, [data, itemName]);

  return {
    ...mergedData,
    lastElementRef,
    hasNextPage: enabled && hasNextPage,
    isLoading: enabled && isLoading,
    ...props,
  };
};

export default useInfiniteFetch;
